/**
 * Skills Configuration for Interactive Game
 * 18 skills total: 13 generators + 5 multipliers
 */

import type { SkillConfig } from '../types'

export const SKILLS_CONFIG: SkillConfig[] = [
  // ==================== TIER 1: Foundational (Free Start) ====================
  {
    id: 'linux',
    name: 'Linux',
    category: 'ops',
    level: 'foundational',
    tier: 1,
    unlocked: false,
    cost: { resource: 'ops', amount: 0 }, // Free starter skill
    generation: { resource: 'ops', rate: 0.6 }, // Balanced for gradual progression
    description: 'Foundation of modern infrastructure',
    evidence: ['Daily CLI usage', 'System administration'],
    flavorText: 'Every DevOps journey starts here',
  },
  {
    id: 'yaml',
    name: 'YAML',
    category: 'ops',
    level: 'foundational',
    tier: 1,
    unlocked: false,
    cost: { resource: 'ops', amount: 15 }, // Lowered for early game
    generation: { resource: 'ops', rate: 1.0 }, // Balanced progression
    description: 'Configuration as code',
    evidence: ['Kubernetes manifests', 'CI/CD pipelines'],
    flavorText: 'Indentation is important!',
  },

  // ==================== TIER 2: Intermediate ====================
  {
    id: 'python',
    name: 'Python',
    category: 'data',
    level: 'intermediate',
    tier: 2,
    unlocked: false,
    cost: { resource: 'data', amount: 25 }, // Balanced early tier 2
    generation: { resource: 'data', rate: 3.5 }, // Balanced progression
    description: 'Data processing and automation',
    evidence: ['ETL scripts', 'Airflow DAGs'],
    flavorText: 'import success',
  },
  {
    id: 'azure',
    name: 'Azure',
    category: 'cloud',
    level: 'intermediate',
    tier: 2,
    unlocked: false,
    cost: { resource: 'cloud', amount: 30 }, // Balanced early tier 2
    generation: { resource: 'cloud', rate: 7.5 }, // Balanced progression
    description: 'Microsoft cloud platform',
    evidence: ['AZ-900', 'AZ-104 certified'],
    flavorText: 'Cloudy with a chance of DevOps',
  },
  {
    id: 'azure-devops',
    name: 'Azure DevOps',
    category: 'cicd',
    level: 'intermediate',
    tier: 2,
    unlocked: false,
    cost: { resource: 'cicd', amount: 35 }, // Balanced early tier 2
    generation: { resource: 'cicd', rate: 5.5 }, // Balanced progression
    description: 'End-to-end DevOps platform',
    evidence: ['Pipeline automation', 'Release management'],
    flavorText: 'Boards, Repos, Pipelines, oh my!',
  },
  {
    id: 'docker',
    name: 'Docker',
    category: 'cloud',
    level: 'intermediate',
    tier: 2,
    unlocked: false,
    cost: { resource: 'cloud', amount: 50 }, // Balanced mid tier 2
    generation: { resource: 'cloud', rate: 6.0 }, // Balanced progression
    description: 'Containerization platform',
    evidence: ['Multi-stage builds', 'Image optimization'],
    flavorText: 'It works on my container',
  },
  {
    id: 'terraform',
    name: 'Terraform',
    category: 'cloud',
    level: 'intermediate',
    tier: 2,
    unlocked: false,
    cost: { resource: 'cloud', amount: 60 }, // Balanced mid tier 2
    generation: { resource: 'cloud', rate: 3.0 }, // Balanced progression
    description: 'Infrastructure as Code',
    evidence: ['Azure resource provisioning', 'State management'],
    flavorText: 'terraform apply --auto-approve (carefully!)',
  },
  {
    id: 'sql',
    name: 'SQL',
    category: 'data',
    level: 'intermediate',
    tier: 2,
    unlocked: false,
    cost: { resource: 'data', amount: 50 }, // Balanced mid tier 2
    generation: { resource: 'data', rate: 2.5 }, // Balanced progression
    description: 'Database querying and management',
    evidence: ['Complex joins', 'Query optimization'],
    flavorText: 'SELECT * FROM success',
  },
  {
    id: 'aws',
    name: 'AWS',
    category: 'cloud',
    level: 'intermediate',
    tier: 2,
    unlocked: false,
    cost: { resource: 'cloud', amount: 40 },
    generation: { resource: 'cloud', rate: 8.0 },
    description: 'Amazon Web Services cloud platform',
    evidence: ['EC2', 'S3', 'Lambda'],
    flavorText: 'The cloud that started it all',
  },
  {
    id: 'postgresql',
    name: 'PostgreSQL',
    category: 'data',
    level: 'intermediate',
    tier: 2,
    unlocked: false,
    cost: { resource: 'data', amount: 45 },
    generation: { resource: 'data', rate: 2.0 },
    description: 'Advanced open-source relational database',
    evidence: ['JSONB', 'Advanced indexing', 'Replication'],
    flavorText: "The world's most advanced open source database",
  },
  {
    id: 'ansible',
    name: 'Ansible',
    category: 'ops',
    level: 'intermediate',
    tier: 2,
    unlocked: false,
    cost: { resource: 'ops', amount: 40 },
    generation: { resource: 'ops', rate: 1.8 },
    description: 'Automation and configuration management',
    evidence: ['Playbooks', 'Infrastructure automation'],
    flavorText: 'Simple, agentless automation',
  },
  {
    id: 'bash',
    name: 'Bash Scripting',
    category: 'ops',
    level: 'intermediate',
    tier: 2,
    unlocked: false,
    cost: { resource: 'ops', amount: 35 },
    generation: { resource: 'ops', rate: 2.5 },
    requires: ['linux'],
    description: 'Shell scripting and automation',
    evidence: ['Automation scripts', 'System administration'],
    flavorText: '#!/bin/bash - The universal glue',
  },
  {
    id: 'jenkins',
    name: 'Jenkins',
    category: 'cicd',
    level: 'intermediate',
    tier: 2,
    unlocked: false,
    cost: { resource: 'cicd', amount: 50 },
    generation: { resource: 'cicd', rate: 4.0 },
    description: 'Automation server for CI/CD',
    evidence: ['Pipeline as code', 'Build automation'],
    flavorText: 'The classic CI/CD workhorse',
  },

  // ==================== TIER 3: Professional ====================
  {
    id: 'airflow',
    name: 'Apache Airflow',
    category: 'data',
    level: 'professional',
    tier: 3,
    unlocked: false,
    cost: { resource: 'data', amount: 200 },
    generation: { resource: 'data', rate: 2.5 },
    description: 'Workflow orchestration',
    evidence: ['DAG development', 'CityPulse ETL project'],
    flavorText: 'Your pipelines are now scheduled',
  },
  {
    id: 'kubernetes',
    name: 'Kubernetes',
    category: 'cloud',
    level: 'professional',
    tier: 3,
    unlocked: false,
    cost: { resource: 'cloud', amount: 250 },
    generation: { resource: 'cloud', rate: 5.5 },
    description: 'Container orchestration',
    evidence: ['Cluster management', 'Service mesh'],
    flavorText: 'kubectl get awesome',
  },
  {
    id: 'gitlab-ci',
    name: 'GitLab CI',
    category: 'cicd',
    level: 'professional',
    tier: 3,
    unlocked: false,
    cost: { resource: 'cicd', amount: 225 },
    generation: { resource: 'cicd', rate: 3.5 },
    description: 'GitLab CI/CD pipelines',
    evidence: ['Pipeline optimization', 'Runner configuration'],
    flavorText: 'Merge requests welcome',
  },
  {
    id: 'monitoring',
    name: 'Monitoring & Observability',
    category: 'ops',
    level: 'professional',
    tier: 3,
    unlocked: false,
    cost: { resource: 'ops', amount: 300 },
    generation: { resource: 'ops', rate: 4.0 },
    description: 'System monitoring and alerts',
    evidence: ['Prometheus', 'Grafana dashboards'],
    flavorText: 'If you can measure it, you can improve it',
  },
  {
    id: 'helm',
    name: 'Helm',
    category: 'ops',
    level: 'professional',
    tier: 3,
    unlocked: false,
    cost: { resource: 'ops', amount: 280 },
    generation: { resource: 'ops', rate: 3.5 },
    requires: ['kubernetes'],
    description: 'Kubernetes package manager',
    evidence: ['Chart development', 'Application deployment'],
    flavorText: 'The package manager for Kubernetes',
  },
  {
    id: 'gcp',
    name: 'Google Cloud Platform',
    category: 'cloud',
    level: 'professional',
    tier: 3,
    unlocked: false,
    cost: { resource: 'cloud', amount: 300 },
    generation: { resource: 'cloud', rate: 6.0 },
    description: 'Google cloud infrastructure',
    evidence: ['GKE', 'BigQuery', 'Cloud Functions'],
    flavorText: 'Powered by Google infrastructure',
  },
  {
    id: 'dbt',
    name: 'dbt',
    category: 'data',
    level: 'professional',
    tier: 3,
    unlocked: false,
    cost: { resource: 'data', amount: 250 },
    generation: { resource: 'data', rate: 3.0 },
    requires: ['sql'],
    description: 'Data transformation tool',
    evidence: ['Analytics engineering', 'Data modeling'],
    flavorText: 'Transform data in your warehouse',
  },
  {
    id: 'snowflake',
    name: 'Snowflake',
    category: 'data',
    level: 'professional',
    tier: 3,
    unlocked: false,
    cost: { resource: 'data', amount: 280 },
    generation: { resource: 'data', rate: 3.5 },
    description: 'Cloud data warehouse',
    evidence: ['Data warehousing', 'Zero-copy cloning'],
    flavorText: 'No two snowflakes are alike',
  },
  {
    id: 'typescript',
    name: 'TypeScript',
    category: 'data',
    level: 'professional',
    tier: 3,
    unlocked: false,
    cost: { resource: 'data', amount: 220 },
    generation: { resource: 'data', rate: 2.8 },
    description: 'Typed JavaScript for large-scale applications',
    evidence: ['Type safety', 'This portfolio!'],
    flavorText: 'JavaScript that scales',
  },
  {
    id: 'redis',
    name: 'Redis',
    category: 'data',
    level: 'professional',
    tier: 3,
    unlocked: false,
    cost: { resource: 'data', amount: 240 },
    generation: { resource: 'data', rate: 2.2 },
    description: 'In-memory data structure store',
    evidence: ['Caching', 'Session management', 'Pub/Sub'],
    flavorText: 'Speed at the speed of RAM',
  },
  {
    id: 'elasticsearch',
    name: 'Elasticsearch',
    category: 'data',
    level: 'professional',
    tier: 3,
    unlocked: false,
    cost: { resource: 'data', amount: 280 },
    generation: { resource: 'data', rate: 2.6 },
    description: 'Distributed search and analytics engine',
    evidence: ['Full-text search', 'Log analytics'],
    flavorText: 'Search at scale',
  },

  // ==================== TIER 4: Advanced ====================
  {
    id: 'github-actions',
    name: 'GitHub Actions',
    category: 'cicd',
    level: 'professional',
    tier: 4,
    unlocked: false,
    cost: { resource: 'cicd', amount: 1200 },
    generation: { resource: 'cicd', rate: 2.5 },
    description: 'GitHub native CI/CD',
    evidence: ['Workflow automation', 'This portfolio!'],
    flavorText: 'Actions speak louder than words',
  },
  {
    id: 'spark',
    name: 'Apache Spark',
    category: 'data',
    level: 'professional',
    tier: 4,
    unlocked: false,
    cost: { resource: 'data', amount: 2000 },
    generation: { resource: 'data', rate: 1.8 },
    description: 'Big data processing',
    evidence: ['Distributed computing', 'Data transformation'],
    flavorText: 'Ignite your data processing',
  },
  {
    id: 'databricks',
    name: 'Databricks',
    category: 'data',
    level: 'professional',
    tier: 4,
    unlocked: false,
    cost: { resource: 'data', amount: 2500 },
    generation: { resource: 'data', rate: 2.0 },
    requires: ['spark'],
    description: 'Unified analytics platform',
    evidence: ['Lakehouse architecture', 'Collaborative notebooks'],
    flavorText: 'Apache Spark, supercharged',
  },
  {
    id: 'kafka',
    name: 'Apache Kafka',
    category: 'data',
    level: 'professional',
    tier: 4,
    unlocked: false,
    cost: { resource: 'data', amount: 2200 },
    generation: { resource: 'data', rate: 1.5 },
    description: 'Distributed event streaming platform',
    evidence: ['Real-time data pipelines', 'Event-driven architecture'],
    flavorText: 'Stream all the things',
  },
  {
    id: 'argocd',
    name: 'ArgoCD',
    category: 'cicd',
    level: 'professional',
    tier: 4,
    unlocked: false,
    cost: { resource: 'cicd', amount: 1500 },
    generation: { resource: 'cicd', rate: 3.0 },
    requires: ['kubernetes'],
    description: 'GitOps continuous delivery for Kubernetes',
    evidence: ['Declarative deployments', 'Automated sync'],
    flavorText: 'GitOps made easy',
  },
  {
    id: 'go',
    name: 'Go',
    category: 'ops',
    level: 'professional',
    tier: 4,
    unlocked: false,
    cost: { resource: 'ops', amount: 1800 },
    generation: { resource: 'ops', rate: 2.5 },
    description: 'Systems programming language',
    evidence: ['Microservices', 'CLI tools', 'Concurrency'],
    flavorText: 'Simple, fast, and reliable',
  },

  // ==================== TIER 4: Soft Skills (Multipliers) ====================
  {
    id: 'adaptability',
    name: 'Adaptability',
    category: 'soft',
    level: 'professional',
    tier: 4,
    unlocked: false,
    cost: { resource: 'ops', amount: 750 },
    multiplier: { type: 'crossCategory', value: 1.2 },
    description: 'Quick to learn new technologies',
    evidence: ['DevOps to Data transition', 'Multiple tech stacks'],
    flavorText: 'Embrace change, thrive in chaos',
  },
  {
    id: 'self-learning',
    name: 'Self-Learning',
    category: 'soft',
    level: 'professional',
    tier: 4,
    unlocked: false,
    cost: { resource: 'data', amount: 1250 },
    multiplier: { type: 'skillUpgrade', value: 1.5 },
    description: 'Continuous self-improvement',
    evidence: ['4 certifications', 'Bootcamp graduate'],
    flavorText: 'Knowledge is power',
  },
  {
    id: 'passionate',
    name: 'Passionate',
    category: 'soft',
    level: 'professional',
    tier: 4,
    unlocked: false,
    cost: { resource: 'cloud', amount: 1500 },
    multiplier: { type: 'activePlay', value: 1.1 },
    description: 'Love for technology and innovation',
    evidence: ['Side projects', 'Open source contributions'],
    flavorText: 'Do what you love',
  },

  // ==================== TIER 5: Expert (Multipliers) ====================
  {
    id: 'responsibility',
    name: 'Responsibility',
    category: 'soft',
    level: 'professional',
    tier: 5,
    unlocked: false,
    cost: { resource: 'cicd', amount: 2000 },
    description: 'Ownership and accountability',
    evidence: ['Production support', 'Critical systems management'],
    flavorText: 'With great power comes great responsibility',
  },
  {
    id: 'team-spirit',
    name: 'Team Spirit',
    category: 'soft',
    level: 'professional',
    tier: 5,
    unlocked: false,
    cost: { resource: 'ops', amount: 2500 },
    multiplier: { type: 'global', value: 2.5 },
    requires: ['responsibility'],
    description: 'Collaboration and knowledge sharing',
    evidence: ['Cross-team collaboration', 'Mentorship'],
    flavorText: 'Together we achieve more',
  },

  // ==================== TIER 6: Ultimate Achievement ====================
  {
    id: 'dream-job',
    name: '🎯 Dream Job',
    category: 'soft',
    level: 'professional',
    tier: 6,
    unlocked: false,
    cost: { resource: 'ops', amount: 15000 }, // Balanced for 8-12 min gameplay
    requires: ['team-spirit', 'kubernetes', 'airflow', 'spark'], // Requires mastery
    description: 'Senior DevOps / Data Engineer position',
    evidence: [
      'Complete skill mastery',
      'Production-ready experience',
      'Leadership capabilities',
      'Full-stack DevOps & Data expertise',
    ],
    flavorText: "🏆 You've mastered the journey. Welcome to your dream role!",
  },
]

/**
 * Helper to get skill by ID
 */
export function getSkillById(id: string): SkillConfig | undefined {
  return SKILLS_CONFIG.find((skill) => skill.id === id)
}

/**
 * Helper to get skills by tier
 */
export function getSkillsByTier(tier: number): SkillConfig[] {
  return SKILLS_CONFIG.filter((skill) => skill.tier === tier)
}

/**
 * Helper to get skills by category
 */
export function getSkillsByCategory(category: string): SkillConfig[] {
  return SKILLS_CONFIG.filter((skill) => skill.category === category)
}
